/**
 * TourProvider — wraps the app with react-joyride for in-app walkthrough.
 * Uses the useTutorial hook for state and provides tour context to children.
 */
import React, { createContext, useContext, useRef } from 'react';
import { Joyride, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import type { EventData } from 'react-joyride';
import { useLanguage } from '../i18n/LanguageContext';
import { useTutorial } from '../hooks/useTutorial';

interface TourContextType {
  startTour: () => void;
  isRunning: boolean;
  needsTour: boolean;
  /** True when tour is active — components should show mock/demo data */
  isTourMode: boolean;
  /** Current step index */
  tourStepIndex: number;
  /** Current step's tourEvent identifier (if any) */
  tourEvent: string | undefined;
}

const TourContext = createContext<TourContextType>({
  startTour: () => {},
  isRunning: false,
  needsTour: false,
  isTourMode: false,
  tourStepIndex: 0,
  tourEvent: undefined,
});

export const useTour = () => useContext(TourContext);

interface TourProviderProps {
  children: React.ReactNode;
}

const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const { t } = useLanguage();
  const {
    isRunning,
    stepIndex,
    steps,
    startTour,
    stopTour,
    handleStepChange,
    handleTourComplete,
    needsTour,
  } = useTutorial();

  // Guard against duplicate STEP_AFTER events during route transitions
  const isTransitioning = useRef(false);

  // Convert our TourStep[] to Joyride Step[] with translated text
  const joyrideSteps = steps.map((step) => ({
    target: step.target,
    title: t(step.titleKey),
    content: t(step.contentKey),
    placement: (step.placement || 'bottom') as 'top' | 'bottom' | 'left' | 'right' | 'center',
    skipBeacon: true,
    showProgress: true,
    primaryColor: '#2563eb',
    buttons: ['back' as const, 'skip' as const, 'primary' as const],
    overlayClickAction: false as const,
  }));

  const handleJoyrideEvent = (data: EventData) => {
    const { status, action, index, type } = data;

    // Tour finished or skipped
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      isTransitioning.current = false;
      handleTourComplete();
      return;
    }

    // User closed the tooltip
    if (action === ACTIONS.CLOSE) {
      isTransitioning.current = false;
      stopTour();
      return;
    }

    // Step changed (next/prev)
    if (type === EVENTS.STEP_AFTER) {
      // Skip if we're in the middle of a route transition
      if (isTransitioning.current) return;

      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
      if (nextIndex >= steps.length) {
        // Last step — tour is done
        isTransitioning.current = false;
        handleTourComplete();
        return;
      }
      if (nextIndex >= 0) {
        // Check if the next step requires route navigation
        const needsNavigation = steps[nextIndex].route && steps[nextIndex].route !== steps[index]?.route;
        if (needsNavigation) {
          isTransitioning.current = true;
          // Clear transition flag after the route change settles
          setTimeout(() => { isTransitioning.current = false; }, 600);
        }
        handleStepChange(nextIndex);
      }
    }

    // Target not found — skip forward only, but not for steps with tourEvents (mock data needs time to render)
    if (type === EVENTS.TARGET_NOT_FOUND) {
      const currentStep = steps[index];
      if (currentStep?.tourEvent) {
        // tourEvent step — mock data is rendering, don't skip
        return;
      }
      if (action !== ACTIONS.PREV) {
        const nextIndex = index + 1;
        if (nextIndex < steps.length) {
          handleStepChange(nextIndex);
        } else {
          handleTourComplete();
        }
      }
    }
  };

  return (
    <TourContext.Provider value={{ startTour, isRunning, needsTour, isTourMode: isRunning, tourStepIndex: stepIndex, tourEvent: steps[stepIndex]?.tourEvent }}>
      {children}
      <Joyride
        steps={joyrideSteps}
        run={isRunning}
        stepIndex={stepIndex}
        continuous
        onEvent={handleJoyrideEvent}
        locale={{
          back: t('common.back'),
          close: t('common.close'),
          last: t('common.done'),
          next: t('common.next'),
          skip: t('tutorial.skip'),
        }}
        styles={{
          tooltipContainer: {
            textAlign: 'left' as const,
          },
          buttonPrimary: {
            backgroundColor: '#2563eb',
            borderRadius: '6px',
            padding: '8px 16px',
          },
          buttonBack: {
            marginRight: 8,
            color: '#6b7280',
          },
          buttonSkip: {
            color: '#9ca3af',
          },
        }}
      />
    </TourContext.Provider>
  );
};

export default TourProvider;
